import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@noor/lib';
import { useLegacySocket, type LegacyChatSocketAction } from '@/entities/legacy-socket';
import {
  merchantChatKeys,
  merchantChatSocketMessageSchema,
  type MerchantChatMessage,
  type MerchantChatThread,
} from '@/entities/merchant-chat';

/**
 * Live chat pushes for the drawer — the old `Routes.tsx` dispatch table,
 * ported onto the shared legacy socket.
 *
 * - `chat_message` for the OPEN thread appends to its cache (deduped by id,
 *   newest-first like the REST slice); anything else toasts, the way the old
 *   panel toasted whenever it wasn't already looking at a chat.
 * - `new_chat` toasts and refetches the list (server owns the ordering — the
 *   old panel unshifted into Redux, but an infinite-query cache has pages).
 * - `new_chat_status` refetches everything a status drives (list badges and
 *   the thread's composer visibility).
 *
 * A push that fails validation is dropped: one malformed frame must not take
 * down the drawer, and the next poll/refetch heals the gap.
 */
export function useMerchantChatEvents({
  openChatId,
  drawerOpen,
}: {
  /** The thread on screen, or null on the list / new-chat screens. */
  openChatId: number | null;
  drawerOpen: boolean;
}) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const handleChatEvent = useCallback(
    (action: LegacyChatSocketAction) => {
      if (action.kind === 'chat-message') {
        const parsed = merchantChatSocketMessageSchema.safeParse(action.content);
        if (!parsed.success) return;
        const incoming = parsed.data;

        if (drawerOpen && openChatId === incoming.chat_id) {
          const message: MerchantChatMessage = {
            id: incoming.id,
            message: incoming.message,
            outgoing: incoming.outgoing,
            seen: false,
            created_at: incoming.created_at,
            files: incoming.files,
          };
          // Every fetched page of this thread (the thread query is cumulative
          // per page, so each cached page gets the newcomer at its head).
          const prefix = [...merchantChatKeys.threads(), incoming.chat_id];
          for (const [key] of queryClient.getQueriesData({ queryKey: prefix })) {
            queryClient.setQueryData<MerchantChatThread>(key, (current) => {
              if (!current || current.data.some((item) => item.id === message.id)) {
                return current;
              }
              return {
                ...current,
                data: [message, ...current.data],
                count: current.count + 1,
              };
            });
          }
        } else {
          toast.info(t('chat.toast.new_message', { message: incoming.message }));
        }
        void queryClient.invalidateQueries({ queryKey: merchantChatKeys.lists() });
        return;
      }

      if (action.kind === 'new-chat') {
        toast.info(t('chat.toast.need_help'));
        void queryClient.invalidateQueries({ queryKey: merchantChatKeys.lists() });
        return;
      }

      void queryClient.invalidateQueries({ queryKey: merchantChatKeys.all });
    },
    [drawerOpen, openChatId, queryClient, t],
  );

  // Mounted with the shell chrome, like the old panel's global provider: the
  // drawer toasts on every authed route, not just while it is open.
  useLegacySocket({ onChatEvent: handleChatEvent, enabled: true });
}
